import React, { useEffect } from 'react';
import './PrivacyPolicy.css';

const PrivacyPolicy = () => {
    useEffect(() => {
        document.title = 'Privacy Policy | Avenis Global';
    }, []);

    return (
        <div className="privacy-policy-page">
            <div className="privacy-hero">
                <div className="container">
                    <h1>Privacy Policy</h1>
                    <p>
                        Your privacy is important to us. Learn how we collect,
                        use, and protect your information.
                    </p>
                    <p className="last-updated">Last updated: February 2025</p>
                </div>
            </div>

            <div className="container">
                <div className="privacy-highlights">
                    <div className="privacy-highlights-grid">
                        <div className="privacy-highlight-item">
                            <div className="privacy-highlight-item-bg"></div>
                            <div className="privacy-highlight-icon privacy-highlight-icon-green">
                                <svg
                                    xmlns="http://www.w3.org/2000/svg"
                                    width="24"
                                    height="24"
                                    viewBox="0 0 24 24"
                                    fill="none"
                                    stroke="currentColor"
                                    strokeWidth="2"
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                >
                                    <path d="M21.801 10A10 10 0 1 1 17 3.335"></path>
                                    <path d="m9 11 3 3L22 4"></path>
                                </svg>
                            </div>
                            <div className="privacy-highlight-title">
                                No Data Sales
                            </div>
                            <div className="privacy-highlight-description">
                                We never sell your personal information
                            </div>
                        </div>

                        <div className="privacy-highlight-item">
                            <div className="privacy-highlight-item-bg"></div>
                            <div className="privacy-highlight-icon privacy-highlight-icon-blue">
                                <svg
                                    xmlns="http://www.w3.org/2000/svg"
                                    width="24"
                                    height="24"
                                    viewBox="0 0 24 24"
                                    fill="none"
                                    stroke="currentColor"
                                    strokeWidth="2"
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                >
                                    <rect
                                        width="18"
                                        height="11"
                                        x="3"
                                        y="11"
                                        rx="2"
                                        ry="2"
                                    ></rect>
                                    <path d="M7 11V7a5 5 0 0 1 10 0v4"></path>
                                </svg>
                            </div>
                            <div className="privacy-highlight-title">
                                Secure Storage
                            </div>
                            <div className="privacy-highlight-description">
                                Industry-standard encryption & security
                            </div>
                        </div>

                        <div className="privacy-highlight-item">
                            <div className="privacy-highlight-item-bg"></div>
                            <div className="privacy-highlight-icon privacy-highlight-icon-purple">
                                <svg
                                    xmlns="http://www.w3.org/2000/svg"
                                    width="24"
                                    height="24"
                                    viewBox="0 0 24 24"
                                    fill="none"
                                    stroke="currentColor"
                                    strokeWidth="2"
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                >
                                    <path d="m16 11 2 2 4-4"></path>
                                    <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2"></path>
                                    <circle cx="9" cy="7" r="4"></circle>
                                </svg>
                            </div>
                            <div className="privacy-highlight-title">
                                Your Control
                            </div>
                            <div className="privacy-highlight-description">
                                Access, modify, or delete your data anytime
                            </div>
                        </div>

                        <div className="privacy-highlight-item">
                            <div className="privacy-highlight-item-bg"></div>
                            <div className="privacy-highlight-icon privacy-highlight-icon-orange">
                                <svg
                                    xmlns="http://www.w3.org/2000/svg"
                                    width="24"
                                    height="24"
                                    viewBox="0 0 24 24"
                                    fill="none"
                                    stroke="currentColor"
                                    strokeWidth="2"
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                >
                                    <circle cx="12" cy="12" r="10"></circle>
                                    <line x1="12" x2="12" y1="8" y2="12"></line>
                                    <line
                                        x1="12"
                                        x2="12.01"
                                        y1="16"
                                        y2="16"
                                    ></line>
                                </svg>
                            </div>
                            <div className="privacy-highlight-title">
                                Transparent
                            </div>
                            <div className="privacy-highlight-description">
                                Clear about what we collect and why
                            </div>
                        </div>
                    </div>
                </div>

                <div className="privacy-content">
                    <div className="privacy-section">
                        <h2>1. Information We Collect</h2>
                        <div className="subsection">
                            <h3>1.1 Personal Information You Provide</h3>
                            <ul>
                                <li>
                                    Company name, business address, and contact
                                    information
                                </li>
                                <li>
                                    Individual contact details (name, email,
                                    phone number)
                                </li>
                                <li>
                                    Business registration and tax identification
                                    numbers
                                </li>
                                <li>
                                    Product categories and sales channel
                                    information
                                </li>
                                <li>
                                    MAP policy documentation and compliance
                                    records
                                </li>
                                <li>
                                    Volume and performance data for partnership
                                    evaluation
                                </li>
                                <li>
                                    Communications, feedback, and support
                                    requests
                                </li>
                            </ul>
                        </div>
                        <div className="subsection">
                            <h3>1.2 Automatically Collected Information</h3>
                            <ul>
                                <li>
                                    IP address, device type, and browser
                                    information
                                </li>
                                <li>
                                    Operating system and technical
                                    specifications
                                </li>
                                <li>
                                    Pages visited, time spent, and navigation
                                    patterns
                                </li>
                                <li>Referral sources and exit page tracking</li>
                                <li>
                                    Date, time, and frequency of website visits
                                </li>
                                <li>Error logs and performance metrics</li>
                            </ul>
                        </div>
                    </div>

                    <div className="privacy-section">
                        <h2>2. How We Use Your Information</h2>

                        <div className="subsection">
                            <h3>2.1 Business Operations and Services</h3>
                            <ul>
                                <li>
                                    Evaluate and process vendor partnership
                                    applications
                                </li>
                                <li>
                                    Manage authorized retail relationships and
                                    compliance
                                </li>
                                <li>
                                    Communicate about products, services, and
                                    opportunities
                                </li>
                                <li>
                                    Send performance reports, updates, and
                                    notifications
                                </li>
                                <li>
                                    Process payments and maintain financial
                                    records
                                </li>
                                <li>
                                    Comply with legal obligations and regulatory
                                    requirements
                                </li>
                            </ul>
                        </div>

                        <div className="subsection">
                            <h3>2.2 Service Improvement and Development</h3>
                            <ul>
                                <li>
                                    Analyze website usage patterns and user
                                    behavior
                                </li>
                                <li>
                                    Improve our services, features, and user
                                    experience
                                </li>
                                <li>
                                    Develop new partnerships and business
                                    opportunities
                                </li>
                                <li>
                                    Ensure platform security, integrity, and
                                    performance
                                </li>
                                <li>
                                    Conduct research and gather market insights
                                </li>
                                <li>
                                    Prevent fraud and maintain service quality
                                </li>
                            </ul>
                        </div>
                    </div>

                    <div className="privacy-section">
                        <h2>3. Information Sharing and Disclosure</h2>

                        <div className="subsection">
                            <h3>3.1 When We May Share Information</h3>
                            <ul>
                                <li>
                                    Service providers who assist with our
                                    operations (hosting, analytics, payment
                                    processing)
                                </li>
                                <li>
                                    Professional advisors (lawyers, accountants,
                                    consultants)
                                </li>
                                <li>
                                    Law enforcement when legally required or for
                                    public safety
                                </li>
                                <li>
                                    Parties involved in business transactions
                                    (mergers, acquisitions)
                                </li>
                                <li>
                                    With your explicit consent for specific
                                    purposes
                                </li>
                                <li>
                                    To protect our rights, property, or safety
                                </li>
                            </ul>
                        </div>

                        <div className="subsection">
                            <h3>3.2 What We Never Do</h3>
                            <ul>
                                <li>
                                    Sell, rent, or trade your personal
                                    information to third parties
                                </li>
                                <li>
                                    Share data for unrelated marketing purposes
                                    without consent
                                </li>
                                <li>
                                    Transfer data without appropriate security
                                    measures
                                </li>
                                <li>
                                    Use your data beyond the stated purposes in
                                    this policy
                                </li>
                                <li>
                                    Retain data longer than necessary for
                                    business purposes
                                </li>
                            </ul>
                        </div>
                    </div>

                    <div className="privacy-section">
                        <h2>4. Data Security and Protection</h2>

                        <div className="subsection">
                            <h3>4.1 Our Security Measures</h3>
                            <ul>
                                <li>
                                    SSL/TLS encryption for all data transmission
                                </li>
                                <li>
                                    Secure servers with multi-layer access
                                    controls
                                </li>
                                <li>
                                    Regular security audits, updates, and
                                    penetration testing
                                </li>
                                <li>
                                    Employee training on data protection and
                                    privacy
                                </li>
                                <li>
                                    Incident response procedures and breach
                                    notification protocols
                                </li>
                                <li>
                                    Physical and electronic access controls and
                                    monitoring
                                </li>
                            </ul>
                        </div>

                        <div className="subsection">
                            <h3>4.2 Your Security Responsibilities</h3>
                            <ul>
                                <li>
                                    Keep your account credentials secure and
                                    confidential
                                </li>
                                <li>
                                    Notify us immediately of any unauthorized
                                    access
                                </li>
                                <li>
                                    Provide accurate and up-to-date information
                                </li>
                                <li>
                                    Use secure networks when accessing our
                                    services
                                </li>
                                <li>
                                    Log out of your account when using shared
                                    devices
                                </li>
                            </ul>
                        </div>
                    </div>

                    <div className="privacy-section">
                        <h2>5. Your Privacy Rights</h2>

                        <div className="subsection">
                            <h3>5.1 Your Rights Under This Policy</h3>
                            <ul>
                                <li>
                                    Access and review your personal information
                                </li>
                                <li>
                                    Request corrections to inaccurate or
                                    incomplete data
                                </li>
                                <li>
                                    Delete your personal information (subject to
                                    legal requirements)
                                </li>
                                <li>
                                    Opt-out of certain communications and
                                    marketing
                                </li>
                                <li>
                                    Request data portability in a
                                    machine-readable format
                                </li>
                                <li>
                                    Lodge complaints with relevant authorities
                                </li>
                                <li>
                                    Withdraw consent for data processing where
                                    applicable
                                </li>
                            </ul>
                        </div>

                        <div className="subsection">
                            <h3>5.2 How to Exercise Your Rights</h3>
                            <ul>
                                <li>
                                    Contact us at compliance@avenisglobal.com
                                    with your request
                                </li>
                                <li>
                                    Provide sufficient information to verify
                                    your identity
                                </li>
                                <li>
                                    Specify the type of request and relevant
                                    data
                                </li>
                                <li>
                                    Allow reasonable time for us to process your
                                    request
                                </li>
                                <li>
                                    We will respond within 30 days of receiving
                                    your request
                                </li>
                            </ul>
                        </div>
                    </div>

                    <div className="privacy-section">
                        <h2>6. Cookies and Tracking Technologies</h2>

                        <div className="subsection">
                            <h3>6.1 How We Use Cookies</h3>
                            <ul>
                                <li>
                                    Essential cookies for website functionality
                                    and security
                                </li>
                                <li>
                                    Preference cookies to remember your settings
                                    and choices
                                </li>
                                <li>
                                    Analytics cookies to understand usage
                                    patterns and performance
                                </li>
                                <li>
                                    Security cookies to prevent fraud and
                                    protect user accounts
                                </li>
                                <li>
                                    Session cookies to maintain your login state
                                </li>
                            </ul>
                        </div>

                        <div className="subsection">
                            <h3>6.2 Managing Cookies and Tracking</h3>
                            <ul>
                                <li>
                                    Adjust browser settings to refuse or delete
                                    cookies
                                </li>
                                <li>Use private/incognito browsing modes</li>
                                <li>
                                    Install browser extensions to block tracking
                                </li>
                                <li>
                                    Note: Some features may not work properly
                                    without cookies
                                </li>
                                <li>
                                    Third-party cookies are subject to their own
                                    privacy policies
                                </li>
                            </ul>
                        </div>
                    </div>

                    <div className="privacy-section">
                        <h2>7. Data Retention and Deletion</h2>

                        <div className="subsection">
                            <h3>7.1 How Long We Keep Your Data</h3>
                            <ul>
                                <li>
                                    Active account data: Retained while your
                                    account is active
                                </li>
                                <li>
                                    Business relationship data: Retained for 7
                                    years for legal compliance
                                </li>
                                <li>
                                    Website analytics: Retained for 2 years for
                                    service improvement
                                </li>
                                <li>
                                    Marketing communications: Retained until you
                                    opt-out
                                </li>
                                <li>
                                    Support communications: Retained for 3 years
                                    for service quality
                                </li>
                            </ul>
                        </div>

                        <div className="subsection">
                            <h3>7.2 Data Deletion Process</h3>
                            <ul>
                                <li>
                                    Account deletion requests processed within
                                    30 days
                                </li>
                                <li>
                                    Data may be retained longer if required by
                                    law
                                </li>
                                <li>
                                    Backup data may persist for additional time
                                </li>
                                <li>
                                    Anonymized data may be retained for
                                    analytics
                                </li>
                                <li>We will confirm deletion in writing</li>
                            </ul>
                        </div>
                    </div>

                    <div className="privacy-section">
                        <h2>8. Contact Us</h2>
                        <p>
                            If you have any questions about this Privacy Policy
                            or our data practices, please contact us:
                        </p>
                        <div className="contact-info">
                            <p>
                                <strong>Email:</strong>{' '}
                                <a
                                    href="mailto:compliance@avenisglobal.com"
                                    className="privacy-contact-link"
                                >
                                    compliance@avenisglobal.com
                                </a>
                            </p>
                            <p>
                                <strong>Phone:</strong>{' '}
                                <a
                                    href="tel:+1"
                                    className="privacy-contact-link"
                                >
                                    +1
                                </a>
                            </p>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default PrivacyPolicy;
